import { createContext } from "react";
import { initializeApp, getApp, getApps, FirebaseApp } from "firebase/app";
import { Functions, getFunctions, httpsCallable } from "firebase/functions";
import { getStorage, ref as stRef, FirebaseStorage, StorageReference } from "firebase/storage";
import { ref, Query, query, equalTo, Database, getDatabase, orderByChild, DatabaseReference } from "firebase/database";
import { Auth, unlink, getAuth, AuthProvider, OAuthProvider, initializeAuth, RecaptchaVerifier, PhoneAuthProvider, updatePhoneNumber, GoogleAuthProvider, PhoneAuthCredential, linkWithPhoneNumber, signInWithPhoneNumber, browserLocalPersistence, browserPopupRedirectResolver } from "firebase/auth";

let getReactNativePersistence: any;

if (typeof document === "undefined") {
    const { getReactNativePersistence: rnPersistence } = require("firebase/auth");
    getReactNativePersistence = rnPersistence;
}

export interface FirebaseConfig {
    apiKey: string;
    projectId: string;
    authDomain: string;
    databaseURL: string;
    storageBucket: string;
    measurementId?: string;
    messagingSenderId: string;
}

export interface FirebaseContextProps {
    app: FirebaseApp;
    database: Database;
    auth: Auth;
    storage: FirebaseStorage;
    functions: Functions;
    config: FirebaseConfig;

    // Auth Providers
    googleProvider: GoogleAuthProvider;
    appleProvider: OAuthProvider;
    phoneProvider: PhoneAuthProvider;
    RecaptchaVerifier: typeof RecaptchaVerifier;

    // Auth Methods
    signInWithPhoneNumber: typeof signInWithPhoneNumber;
    updatePhoneNumber: typeof updatePhoneNumber;
    unlink: typeof unlink;
    linkWithPhoneNumber: typeof linkWithPhoneNumber;
    authRef: typeof getAuth;

    // Credential Methods
    googleCredential: (idToken: string, accessToken: string) => AuthProvider;
    mobileAuthCredential: (
        verificationId: string,
        code: string
    ) => PhoneAuthCredential;

    // Database References
    usersRef: DatabaseReference;
    bookingRef: DatabaseReference;
    cancelreasonRef: DatabaseReference;
    settingsRef: DatabaseReference;
    carTypesRef: DatabaseReference;
    promoRef: DatabaseReference;
    notifyRef: DatabaseReference;
    withdrawRef: DatabaseReference;
    languagesRef: DatabaseReference;
    bannersAddRef: DatabaseReference;
    bannersRef: DatabaseReference;
    carAddRef: DatabaseReference;
    panicAddRef: DatabaseReference;
    allLocationsRef: DatabaseReference;
    clientsRefs: DatabaseReference;

    // Dynamic Database References
    carTypesEditRef: (id: string) => DatabaseReference;
    promoEditRef: (id: string) => DatabaseReference;
    notifyEditRef: (id: string) => DatabaseReference;
    addressRef: (uid: string, id: string) => DatabaseReference;
    addressEditRef: (uid: string) => DatabaseReference;
    singleUserRef: (uid: string) => DatabaseReference;
    driversRef: Query;
    singleBookingRef: (bookingKey: string) => DatabaseReference;
    requestedDriversRef: (bookingKey: string) => DatabaseReference;
    referralIdRef: (referralId: string) => Query;
    trackingRef: (bookingId: string) => DatabaseReference;
    tasksRef: () => Query;
    singleTaskRef: (uid: string, bookingId: string) => DatabaseReference;
    bookingListRef: (uid: string, role: string) => DatabaseReference | Query;
    chatRef: (bookingId: string) => DatabaseReference;
    languagesEditRef: (id: string) => DatabaseReference;
    walletHistoryRef: (uid: string) => DatabaseReference;
    userNotificationsRef: (uid: string) => DatabaseReference;
    userNotificationsItemRef: (userId: string, id: string) => DatabaseReference;
    userRatingsRef: (uid: string) => DatabaseReference;
    bannersEditRef: (uid: string) => DatabaseReference;
    carsRef: (uid: string, role: string) => DatabaseReference | Query;
    carEditRef: (id: string) => DatabaseReference;
    panicEditRef: (id: string) => DatabaseReference;
    userLocationRef: (uid: string) => DatabaseReference;
    userlocationsCostumerRef: (uid: string) => DatabaseReference;
    addClientsRefs: (uid: string) => DatabaseReference;
    clientsEditRef: (uid: string) => DatabaseReference;

    plansRef: DatabaseReference,
    plansAddRef: DatabaseReference,
    plansEditRef: (uid: string) => DatabaseReference,

    admSavedAddressesRef: DatabaseReference,
    admSavedAddressesAddRef: DatabaseReference,
    admSavedAddressesEditRef: (uid: string) => DatabaseReference,

    // Storage References
    carDocImage: (id: string) => StorageReference;
    profileImageRef: (uid: string) => StorageReference;
    uploadImageTaxi: (uid: string) => StorageReference;
    verifyIdImageRef: (uid: string) => StorageReference;
    verifyIdImageSelfieRef: (uid: string) => StorageReference;
    bookingImageRef: (bookingId: string, imageType: string) => StorageReference;
    driverDocsRef: (uid: string) => StorageReference;
    driverDocsRefBack: (uid: string) => StorageReference;
    criminalBackgroundCheckCertificateRef: (uid: string) => StorageReference;
    bannersImage: (id: string) => StorageReference;
    carImage: (id: string, index: number) => StorageReference;
    clientsEditImageRef: (id: string) => StorageReference;

    // Cloud Functions
    callFunction: <T = any>(name: string, data?: any) => Promise<T>;
}

// Context
const FirebaseContext = createContext<FirebaseContextProps | null>(null);

// Helper function to create the full structure
const createFullStructure = (
    app: FirebaseApp,
    db: Database,
    auth: Auth,
    storage: FirebaseStorage,
    config: FirebaseConfig,
): FirebaseContextProps => {
    const functions = getFunctions(app);

    const dbRef = (path: string) => ref(db, `${path}`);
    const dbQuery = (path: string, key: string, value: string) =>
        query(ref(db, `${path}`), orderByChild(key), equalTo(value));
    const storageRef = (path: string) => stRef(storage, `${path}`);

    return {
        app,
        config,
        database: db,
        auth,
        storage,
        functions,

        // Auth Providers
        authRef: getAuth,
        googleProvider: new GoogleAuthProvider(),
        appleProvider: new OAuthProvider("apple.com"),
        phoneProvider: new PhoneAuthProvider(auth),
        RecaptchaVerifier,

        // Auth Methods
        signInWithPhoneNumber,
        updatePhoneNumber,
        unlink,
        linkWithPhoneNumber,

        // Credential Methods
        googleCredential: (idToken: string, accessToken: string) =>
            GoogleAuthProvider.credential(idToken, accessToken),
        mobileAuthCredential: (verificationId: string, code: string) =>
            PhoneAuthProvider.credential(verificationId, code),

        // Database References
        usersRef: dbRef("users"),
        bookingRef: dbRef("bookings"),
        cancelreasonRef: dbRef("cancel_reason"),
        settingsRef: dbRef("settings"),
        carTypesRef: dbRef("cartypes"),
        promoRef: dbRef("promos"),
        notifyRef: dbRef("notifications"),
        withdrawRef: dbRef("withdraws"),
        languagesRef: dbRef("languages"),
        bannersAddRef: dbRef("banners"),
        bannersRef: dbRef("banners"),
        carAddRef: dbRef("cars"),
        panicAddRef: dbRef("panics"),
        allLocationsRef: dbRef("locations"),
        clientsRefs: dbRef("clients"),

        // Dynamic Database References
        carTypesEditRef: (id: string) => dbRef(`cartypes/${id}`),
        promoEditRef: (id: string) => dbRef(`promos/${id}`),
        notifyEditRef: (id: string) => dbRef(`notifications/${id}`),
        addressRef: (uid: string, id: string) => dbRef(`savedAddresses/${uid}/${id}`),
        addressEditRef: (uid: string) => dbRef(`savedAddresses/${uid}`),
        singleUserRef: (uid: string) => dbRef(`users/${uid}`),
        driversRef: dbQuery("users", "usertype", "driver"),
        singleBookingRef: (bookingKey: string) => dbRef(`bookings/${bookingKey}`),
        requestedDriversRef: (bookingKey: string) =>
            dbRef(`bookings/${bookingKey}/requestedDrivers`),
        referralIdRef: (referralId: string) =>
            dbQuery("users", "referralId", referralId),
        trackingRef: (bookingId: string) => dbRef(`tracking/${bookingId}`),
        tasksRef: () => dbQuery("bookings", "status", "NEW"),
        singleTaskRef: (uid: string, bookingId: string) =>
            dbRef(`bookings/${bookingId}/requestedDrivers/${uid}`),
        bookingListRef: (uid: string, role: string) =>
            role === "customer"
                ? dbQuery("bookings", "customer", uid)
                : role === "driver"
                    ? dbQuery("bookings", "driver", uid)
                    : role === "fleetadmin"
                        ? query(ref(db, `bookings`), orderByChild("fleetadmin"))
                        : dbRef("bookings"),
        chatRef: (bookingId: string) => dbRef(`chats/${bookingId}/messages`),
        languagesEditRef: (id: string) => dbRef(`languages/${id}`),
        walletHistoryRef: (uid: string) => dbRef(`walletHistory/${uid}`),
        userNotificationsRef: (uid: string) => dbRef(`userNotifications/${uid}`),
        userNotificationsItemRef: (userId: string, id: string) => ref(db, "userNotifications/" + userId + "/" + id),
        userRatingsRef: (uid: string) => dbRef(`userRatings/${uid}`),
        bannersEditRef: (uid: string) => dbRef(`banners/${uid}`),
        carsRef: (uid: string, role: string) =>
            role === "driver"
                ? dbQuery("cars", "driver", uid)
                : role === "fleetadmin"
                    ? query(ref(db, `cars`), orderByChild("fleetadmin"))
                    : dbRef("cars"),
        carEditRef: (id: string) => dbRef(`cars/${id}`),
        panicEditRef: (id: string) => dbRef(`panics/${id}`),
        userLocationRef: (uid: string) => dbRef(`locations/${uid}`),
        userlocationsCostumerRef: (uid: string) =>
            dbRef(`locationsCostumer/${uid}`),
        addClientsRefs: (uid: string) => dbRef(`clients/${uid}`),
        clientsEditRef: (uid: string) => dbRef(`clients/${uid}`),

        plansRef: dbRef("plans"),
        plansAddRef: dbRef("plans/"),
        plansEditRef: (uid: string) => dbRef(`plans/${uid}`),

        admSavedAddressesRef: dbRef("admSavedAddresses"),
        admSavedAddressesAddRef: dbRef("admSavedAddresses/"),
        admSavedAddressesEditRef: (uid: string) => dbRef(`admSavedAddresses/${uid}`),

        // Storage References
        carDocImage: (id: string) => storageRef(`cartypes/${id}`),
        profileImageRef: (uid: string) => storageRef(`users/${uid}/profileImage`),
        uploadImageTaxi: (uid: string) => storageRef(`taximeter/${uid}/profileImage`),
        verifyIdImageRef: (uid: string) => storageRef(`users/${uid}/verifyIdImage`),
        verifyIdImageSelfieRef: (uid: string) =>
            storageRef(`users/${uid}/verifyIdImageSelfie`),
        bookingImageRef: (bookingId: string, imageType: string) =>
            storageRef(`bookings/${bookingId}/${imageType}`),
        driverDocsRef: (uid: string) => storageRef(`users/${uid}/license`),
        driverDocsRefBack: (uid: string) => storageRef(`users/${uid}/licenseBack`),
        criminalBackgroundCheckCertificateRef: (uid: string) =>
            storageRef(`users/${uid}/criminalBackgroundCheckCertificate`),
        bannersImage: (id: string) => storageRef(`banners/${id}`),
        carImage: (id: string, index: number) => storageRef(`cars/${id}/${index}`),
        clientsEditImageRef: (id: string) => storageRef(`clients/${id}`),

        // Cloud Functions
        callFunction: async <T = any,>(name: string, data?: any): Promise<T> => {
            if (!functions) throw new Error("Firebase functions not initialized");
            const func = httpsCallable(functions, name);
            const result = await func(data);
            return result.data as T;
        },
    };
};

// Default Firebase instance
let firebase: FirebaseContextProps;

// Firebase Provider Props
interface FirebaseProviderProps {
    children: React.ReactNode;
    config: FirebaseConfig;
    AsyncStorage?: any;
}

const FirebaseProvider: React.FC<FirebaseProviderProps> = ({
    children,
    AsyncStorage,
    config,
}) => {
    let app: FirebaseApp,
        auth: Auth,
        database: Database,
        storage: FirebaseStorage;

    if (!getApps().length) {
        try {
            app = initializeApp(config);
            const isReactNative = typeof document === 'undefined' && AsyncStorage;

            if (isReactNative) {
                auth = initializeAuth(app, {
                    persistence: getReactNativePersistence(AsyncStorage),
                });
            } else {
                auth = initializeAuth(app, {
                    persistence: browserLocalPersistence,
                    popupRedirectResolver: browserPopupRedirectResolver,
                });
            }
            database = getDatabase(app);
            storage = getStorage(app);
        } catch (error) {
            console.error("Erro inicializando Firebase: " + error);
            throw error;
        }
    } else {
        app = getApp();
        auth = getAuth(app);
        database = getDatabase(app);
        storage = getStorage(app);
    }

    firebase = createFullStructure(app, database, auth, storage, config);

    return (
        <FirebaseContext.Provider value={firebase}>
            {children}
        </FirebaseContext.Provider>
    );
};

export { firebase, FirebaseProvider, FirebaseContext };